/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CheckEmailAddress } from '../models/CheckEmailAddress';
import type { CheckEmailAddressRequest } from '../models/CheckEmailAddressRequest';
import type { LegalDocumentAcceptanceRequestRequest } from '../models/LegalDocumentAcceptanceRequestRequest';
import type { PaginatedLegalDocumentAcceptanceList } from '../models/PaginatedLegalDocumentAcceptanceList';
import type { PaginatedLegalDocumentList } from '../models/PaginatedLegalDocumentList';
import type { UserCompaniesList } from '../models/UserCompaniesList';
import type { UserId } from '../models/UserId';
import type { UserLanguage } from '../models/UserLanguage';
import type { UserLanguageRequest } from '../models/UserLanguageRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class UserService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * @returns PaginatedLegalDocumentAcceptanceList
     * @throws ApiError
     */
    public userAcceptancesList({
        limit,
        offset,
    }: {
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedLegalDocumentAcceptanceList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/user/acceptances/',
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * @returns PaginatedLegalDocumentAcceptanceList
     * @throws ApiError
     */
    public userAcceptancesCreate({
        requestBody,
        limit,
        offset,
    }: {
        requestBody: LegalDocumentAcceptanceRequestRequest,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedLegalDocumentAcceptanceList> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/user/acceptances/',
            query: {
                'limit': limit,
                'offset': offset,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns CheckEmailAddress
     * @throws ApiError
     */
    public userCheckEmailAddressCreate({
        requestBody,
    }: {
        requestBody: CheckEmailAddressRequest,
    }): CancelablePromise<CheckEmailAddress> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/user/check-email-address/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns UserCompaniesList
     * @throws ApiError
     */
    public userCompaniesRetrieve(): CancelablePromise<UserCompaniesList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/user/companies/',
        });
    }

    /**
     * @returns UserId
     * @throws ApiError
     */
    public userIdRetrieve(): CancelablePromise<UserId> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/user/id/',
        });
    }

    /**
     * @returns UserLanguage
     * @throws ApiError
     */
    public userLanguageRetrieve(): CancelablePromise<UserLanguage> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/user/language/',
        });
    }

    /**
     * @returns UserLanguage
     * @throws ApiError
     */
    public userLanguageUpdate({
        requestBody,
    }: {
        requestBody?: UserLanguageRequest,
    }): CancelablePromise<UserLanguage> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/user/language/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns PaginatedLegalDocumentList
     * @throws ApiError
     */
    public userLegalDocumentsList({
        audience,
        limit,
        offset,
    }: {
        /**
         * Available values: Employee, Company Manager. Seperate multiple values with a comma.
         */
        audience?: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedLegalDocumentList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/user/legal-documents/',
            query: {
                'audience': audience,
                'limit': limit,
                'offset': offset,
            },
        });
    }

}
